// src/pages/Dashboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Typography, Table, message, Input, Flex } from 'antd';
import { ReloadOutlined, SearchOutlined, EyeOutlined, TeamOutlined } from '@ant-design/icons';
import { userAPI, GetUserListParams } from '../api/axios';
import { User } from '../types/user';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { Content } from 'antd/es/layout/layout';
import Layout from './Layout';
const { Title } = Typography;
const { Search } = Input;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [sortField, setSortField] = useState<string>('user_id');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');

  const fetchUsers = async (params?: Partial<GetUserListParams>) => {
    setLoading(true);
    try {
      const currentPage = params?.Offset !== undefined ? params.Offset : pagination.current;
      const pageSize = params?.Limit || pagination.pageSize;
      const offset = (currentPage - 1) * pageSize;

      const requestParams: GetUserListParams = {
        SearchText: params?.SearchText !== undefined ? params.SearchText : searchText,
        SortColumn: params?.SortColumn || sortField,
        SortDirection: params?.SortDirection || sortOrder,
        Limit: pageSize,
        Offset: offset,
      };

      console.log('📊 Dashboard fetching with params:', requestParams);
      const response = await userAPI.getUserList(requestParams);
      console.log('✅ Dashboard received response:', response);

      if (response.success && response.result) {
        const userList = response.result.UserList || [];
        const totalCount = response.result.TotalCount || 0;

        const mappedUsers = userList.map((apiUser: any) => ({
          id: apiUser.Id || apiUser.user_id,
          email: apiUser.Email || apiUser.email,
          name: apiUser.Name || apiUser.name,
          status: apiUser.Status || apiUser.status,
          role: apiUser.Role || apiUser.role,
          session_count: apiUser.session_count,
          rowNo: apiUser.RowNo || apiUser.row_no,
          joining_date: apiUser.joining_date,
          stripe_id: apiUser.stripe_id,
        }));

        setUsers(mappedUsers);
        setTotal(totalCount);
      } else {
        message.warning('No users found or unexpected response format');
        setUsers([]);
        setTotal(0);
      }
    } catch (error: any) {
      console.error('❌ Failed to fetch users:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to fetch users');
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (
    newPagination: TablePaginationConfig,
    filters: any,
    sorter: SorterResult<User> | SorterResult<User>[]
  ) => {
    const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const newPage = newPagination.current || 1;
    const newPageSize = newPagination.pageSize || 10;

    setPagination({
      current: newPage,
      pageSize: newPageSize,
    });

    let newSortField = sortField;
    let newSortOrder = sortOrder;

    if (currentSorter.field) {
      newSortField = String(currentSorter.field);
      newSortOrder = currentSorter.order === 'ascend' ? 'ASC' : 'DESC';
      setSortField(newSortField);
      setSortOrder(newSortOrder);
    }

    fetchUsers({
      Limit: newPageSize,
      Offset: newPage,
      SortColumn: newSortField,
      SortDirection: newSortOrder,
      SearchText: searchText,
    });
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
    fetchUsers({
      SearchText: value,
      Offset: 1,
      Limit: pagination.pageSize,
    });
  };

  const handleRefresh = () => {
    setPagination({ current: 1, pageSize: 10 });
    setSearchText('');
    setSortField('user_id');
    setSortOrder('ASC');
    fetchUsers({
      SearchText: '',
      SortColumn: 'user_id',
      SortDirection: 'ASC',
      Limit: 10,
      Offset: 1,
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const handleViewSubscription = (stripeId: string, userId: number) => {
    if (!stripeId) {
      message.warning(`User ID ${userId} does not have a Stripe Customer ID`);
      return;
    }
    navigate(`/subscriptions/${stripeId}/${userId}`);
  };

  const columns: ColumnsType<User> = [
    {
      title: 'User ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      sorter: true,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (name: string) => name || '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      sorter: true,
      render: (role: string) => role || 'User',
    },
    {
      title: 'Joining Date',
      dataIndex: 'joining_date',
      key: 'joining_date',
      width: 150,
      sorter: true,
      render: (joiningDate: string) => formatDate(joiningDate),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      sorter: true,
      render: (status: string) => (
        <span
          style={{
            color: status === 'Active' ? '#52c41a' : '#ff4d4f',
            fontWeight: 500,
          }}
        >
          {status || 'Active'}
        </span>
      ),
    },
    {
      title: 'Sessions',
      dataIndex: 'session_count',
      key: 'session_count',
      width: 100,
      align: 'center',
      sorter: true,
    },
    {
      title: 'Subscription',
      dataIndex: 'stripe_id',
      key: 'stripe_id',
      width: 140,
      align: 'center',
      render: (stripeId: string, record: User) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewSubscription(stripeId, record.id)}
          disabled={!stripeId}
          className="btnCustom btnPrimary"
        >
          View Plan
        </Button>
      ),
    },
  ];

  return (
    <Layout pageTitle="User Management">
      <Content className="mainBodylayout">
        <div className="mainBodyInnerContainer">
          <Flex vertical gap={24}>
            <Flex align="center" justify="space-between">
              <Title level={4} style={{ margin: 0 }}>
                <TeamOutlined /> User List
              </Title>
              <div style={{ display: 'flex', gap: 10 }}>
                <Search
                  placeholder="Search users..."
                  allowClear
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={handleSearch}
                  style={{ width: 250 }}
                  enterButton={
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      rootClassName="addonBtnStyle btnCustom btnPrimary"
                    />
                  }
                />
                <Button
                  type="primary"
                  className="btnCustom btnPrimary"
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                  loading={loading}
                >
                  Refresh
                </Button>
              </div>
            </Flex>

            <Table
              columns={columns}
              dataSource={users}
              rowKey="id"
              showSorterTooltip={false}
              loading={loading}
              onChange={handleTableChange}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: total,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} users`,
                pageSizeOptions: ['10', '20', '50', '100'],
              }}
              scroll={{ x: 'max-content' }}
              locale={{
                emptyText: (
                  <div style={{ 
                    padding: '60px 0', 
                    color: '#d9d9d9',
                    opacity: 0.6,
                    filter: 'blur(0.3px)'
                  }}>
                    <TeamOutlined style={{ fontSize: 48, marginBottom: 16, display: 'block' }} />
                    <div style={{ fontSize: 16, fontWeight: 500 }}>No Data Available</div>
                  </div>
                )
              }}
            />
          </Flex>
        </div>
      </Content>
    </Layout>
  );
};

export default Dashboard;